import React from 'react';
import {
  Image,
  ImageBackground,
  type ImageSourcePropType,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { glowFor } from './badgeGlow';
import { useTheme } from '../../../context/ThemeContext';
import type { GamificationEvent } from '../../../types/auth';
import { GamificationShareButton } from '../../../components/gamification/GamificationShareButton';
import { pluralRu } from '../../master-profile/plural';
import { uiFont } from '../../../styles/agapellaFonts';
import {
  formatMilestoneDays,
  getMilestoneDefinition,
  type MilestoneIconKey,
} from './milestoneCatalog';

import { accentTextOn } from '../../../styles/accentText';
/**
 * CORE-708 — owner-only `milestone_reached` celebration.
 *
 * Ownership is decided by the caller. This component only renders the event it
 * receives and deliberately adds no XP/currency reward. The milestone branch
 * uses the approved 3/7/21/40/108/365 contract and exact Canonical v4 artwork;
 * `new_record` stays on the pre-CORE-708 presentation below.
 */
const MILESTONE_ICONS: Record<MilestoneIconKey, ImageSourcePropType> = {
  flame: require('../../../../assets/gamification/milestone-flame.png'),
  'soul-light': require('../../../../assets/gamification/milestone-soul-light.png'),
  sprout: require('../../../../assets/gamification/milestone-sprout.png'),
  'calendar-plus': require('../../../../assets/gamification/milestone-calendar-plus.png'),
  progress: require('../../../../assets/gamification/milestone-progress.png'),
  'shield-star': require('../../../../assets/gamification/milestone-shield-star.png'),
};

const MILESTONE_BACKGROUND = {
  light: require('../../../../assets/gamification/milestone-background-light.webp'),
  dark: require('../../../../assets/gamification/milestone-background-dark.webp'),
};

const MILESTONE_BACKGROUND_VEIL = {
  light: ['rgba(255,250,244,0.95)', 'rgba(255,250,244,0.72)'] as const,
  dark: ['rgba(28,24,22,0.94)', 'rgba(28,24,22,0.74)'] as const,
};

// Approved Agapella typography tokens. Native does not bundle the custom
// faces yet, so it uses the same documented serif/system fallbacks as auth.
const MILESTONE_BRAND_FONT = Platform.select({ web: 'var(--ag-font-brand)', ios: 'Georgia', default: 'serif' });

interface Props {
  visible: boolean;
  onClose: () => void;
  event: GamificationEvent | null;
}

export const MilestoneCelebrationModal: React.FC<Props> = ({ visible, onClose, event }) => {
  if (!event) return null;

  if (event.type === 'milestone_reached') {
    return <MilestoneReachedModal visible={visible} onClose={onClose} event={event} />;
  }

  return <LegacyNewRecordModal visible={visible} onClose={onClose} event={event} />;
};

const MilestoneReachedModal: React.FC<{ visible: boolean; onClose: () => void; event: GamificationEvent }> = ({
  visible,
  onClose,
  event,
}) => {
  const { isDark } = useTheme();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const mobile = windowWidth <= 520;
  const targetHeight = mobile ? 748 : 686;
  const baseGutter = mobile ? 8 : 22;
  const safeTop = Platform.OS === 'web' ? 0 : insets.top;
  const safeBottom = Platform.OS === 'web' ? 0 : insets.bottom;
  const verticalGutter = baseGutter * 2 + safeTop + safeBottom;
  const cardHeight = Math.max(220, Math.min(targetHeight, windowHeight - verticalGutter));
  const milestone = getMilestoneDefinition(event.value);

  // The backend contract only emits the six catalog thresholds. A defensive
  // fallback keeps an unexpected event dismissible without assigning it a
  // wrong catalog name; it uses the neutral first medallion until BE is fixed.
  const days = milestone?.threshold ?? event.value ?? 0;
  const name = milestone?.name ?? event.title ?? 'Новая веха';
  const icon = MILESTONE_ICONS[milestone?.icon ?? 'flame'];
  const dayLabel = formatMilestoneDays(days);
  const foreground = isDark ? '#F8EEE5' : '#302C29';
  const muted = isDark ? '#C6B8AE' : '#756C66';
  const rewardBackground = isDark ? 'rgba(43,34,29,0.82)' : 'rgba(255,255,255,0.72)';
  const rewardBorder = isDark ? '#805036' : '#F0BA94';

  return (
    // RN Web's Modal owns the Escape stack and calls onRequestClose only for
    // the active (topmost) modal. A global handler here would incorrectly ACK
    // this event when Escape is meant to close its nested ShareModal.
    <Modal transparent visible={visible} animationType="fade" onRequestClose={onClose} statusBarTranslucent>
      <Pressable
        style={[
          styles.scrim,
          mobile && styles.scrimMobile,
          {
            paddingTop: baseGutter + safeTop,
            paddingBottom: baseGutter + safeBottom,
            paddingHorizontal: baseGutter,
          },
        ]}
        onPress={onClose}
        accessible={false}
      >
        <Pressable
          onPress={(pressEvent) => pressEvent.stopPropagation()}
          accessible={false}
          accessibilityViewIsModal
          style={[
            styles.milestoneCard,
            mobile && styles.milestoneCardMobile,
            {
              height: cardHeight,
              backgroundColor: isDark ? '#1C1816' : '#FFFAF4',
              borderColor: isDark ? '#9C552B' : '#F1A267',
            },
          ]}
        >
          <ImageBackground
            source={isDark ? MILESTONE_BACKGROUND.dark : MILESTONE_BACKGROUND.light}
            resizeMode="cover"
            style={styles.milestoneBackground}
          >
            <LinearGradient
              pointerEvents="none"
              colors={isDark ? MILESTONE_BACKGROUND_VEIL.dark : MILESTONE_BACKGROUND_VEIL.light}
              start={{ x: 0.5, y: 0 }}
              end={{ x: 0.5, y: 1 }}
              style={StyleSheet.absoluteFillObject}
            />
            <TouchableOpacity
              onPress={onClose}
              activeOpacity={0.75}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel="Закрыть"
              style={[styles.milestoneClose, mobile && styles.milestoneCloseMobile]}
            >
              <Ionicons name="close" size={22} color={accentTextOn('#EF6A13')} />
            </TouchableOpacity>

            <ScrollView
              bounces={false}
              showsVerticalScrollIndicator={false}
              style={styles.milestoneScroll}
              contentContainerStyle={[
                styles.milestoneScrollContent,
                mobile && styles.milestoneScrollContentMobile,
              ]}
            >
              <View style={styles.milestoneMain}>
                <Text style={styles.eyebrow}>Пламя Любви</Text>
                <Text
                  accessibilityRole="header"
                  style={[styles.milestoneTitle, mobile && styles.milestoneTitleMobile, { color: foreground }]}
                >
                  Новая веха!
                </Text>
                <Text style={[styles.milestoneSubtitle, { color: muted }]}>
                  Ты поддерживаешь Пламя Любви уже {dayLabel}
                </Text>

                <View
                  style={[
                    styles.heroHalo,
                    mobile && styles.heroHaloMobile,
                    {
                      backgroundColor: isDark ? 'rgba(255,142,54,0.08)' : 'rgba(255,169,67,0.10)',
                      borderColor: isDark ? 'rgba(230,113,30,0.24)' : 'rgba(230,113,30,0.18)',
                    },
                  ]}
                >
                  <View
                    style={[
                      styles.heroHaloInner,
                      mobile && styles.heroHaloInnerMobile,
                      { borderColor: isDark ? 'rgba(230,113,30,0.30)' : 'rgba(230,113,30,0.22)' },
                    ]}
                  >
                    <Image
                      source={icon}
                      resizeMode="contain"
                      style={[styles.heroIcon, mobile && styles.heroIconMobile]}
                      accessibilityLabel={`Бейдж «${name}»`}
                    />
                  </View>
                </View>

                <Text style={[styles.milestoneName, mobile && styles.milestoneNameMobile]}>{name}</Text>
                <Text style={[styles.streakValue, { color: foreground }]}>{dayLabel} подряд</Text>
                <Text style={[styles.milestoneCaption, { color: muted }]}>Новая веха на твоём Пути Света</Text>

                <View style={[styles.unlocked, mobile && styles.unlockedMobile]}>
                  <Text style={[styles.unlockedLabel, { color: muted }]}>Достижение открыто</Text>
                  <View
                    accessible
                    accessibilityLabel={`Достижение открыто. Бейдж «${name}» добавлен в коллекцию достижений`}
                    style={[styles.reward, { backgroundColor: rewardBackground, borderColor: rewardBorder }]}
                  >
                    <Image source={icon} resizeMode="contain" style={styles.rewardIcon} accessible={false} />
                    <View style={styles.rewardCopyBlock}>
                      <Text style={[styles.rewardTitle, { color: foreground }]}>Бейдж «{name}»</Text>
                      <Text style={[styles.rewardCopy, { color: muted }]}>Добавлен в коллекцию достижений</Text>
                    </View>
                  </View>
                </View>
              </View>

              <View style={[styles.milestoneActions, mobile && styles.milestoneActionsMobile]}>
                <TouchableOpacity
                  style={[styles.milestoneSecondary, mobile && styles.milestoneActionMobile]}
                  onPress={onClose}
                  activeOpacity={0.85}
                  accessibilityRole="button"
                  accessibilityLabel="Продолжить путь"
                >
                  <Text style={[styles.milestoneSecondaryText, { color: foreground }]}>Продолжить путь</Text>
                </TouchableOpacity>
                <LinearGradient
                  colors={['#F56B0D', '#FB9628']}
                  start={{ x: 0, y: 0.5 }}
                  end={{ x: 1, y: 0.5 }}
                  style={[styles.milestonePrimaryShell, mobile && styles.milestoneActionMobile]}
                >
                  <GamificationShareButton
                    type="milestone"
                    id={String(days)}
                    filled
                    fallbackMessage={`Новая веха на моём Пути Света в Agapella — «${name}», ${dayLabel} подряд`}
                    style={styles.milestonePrimary}
                    textStyle={styles.milestonePrimaryText}
                  />
                </LinearGradient>
              </View>
            </ScrollView>
          </ImageBackground>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

// Pre-CORE-708 record presentation. Kept intentionally separate so changing
// the milestone artwork cannot silently redesign `new_record`.
const RECORD_STYLE = {
  icon: 'trophy' as const,
  color: '#E0A800',
  tint: 'rgba(224,168,0,0.14)',
  headline: 'Новый рекорд!',
  shareType: 'record' as const,
};

const LegacyNewRecordModal: React.FC<{ visible: boolean; onClose: () => void; event: GamificationEvent }> = ({
  visible,
  onClose,
  event,
}) => {
  const { colors, isDark } = useTheme();
  const days = event.value ?? 0;
  const daysLabel = days > 0 ? `${days} ${pluralRu(days, ['день', 'дня', 'дней'])} подряд` : null;

  return (
    <Modal transparent visible={visible} animationType="fade" onRequestClose={onClose}>
      <View style={styles.legacyScrim}>
        <View style={[styles.legacyCard, { backgroundColor: colors.surfaceElevated, borderColor: RECORD_STYLE.color + '55' }]}>
          <TouchableOpacity onPress={onClose} style={[styles.legacyClose, { backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#F3F4F6' }]}>
            <Ionicons name="close" size={18} color={colors.textMuted} />
          </TouchableOpacity>

          <ScrollView contentContainerStyle={styles.legacyBody}>
            <Text style={[styles.legacyCongrats, { color: colors.textPrimary }]}>Поздравляем!</Text>
            <Text style={[styles.legacyReached, { color: colors.textMuted }]}>{RECORD_STYLE.headline}</Text>

            <View style={[styles.legacyBadgeGlow, glowFor(RECORD_STYLE.color)]}>
              <View style={[styles.legacyBadge, { borderColor: RECORD_STYLE.color, backgroundColor: RECORD_STYLE.tint }]}>
                <Ionicons name={RECORD_STYLE.icon} size={60} color={RECORD_STYLE.color} />
              </View>
            </View>

            {daysLabel ? <Text style={[styles.legacyDays, { color: RECORD_STYLE.color }]}>{daysLabel}</Text> : null}
            {event.message ? <Text style={[styles.legacyMessage, { color: colors.textSecondary }]}>{event.message}</Text> : null}

            <View style={styles.legacyActions}>
              <TouchableOpacity style={[styles.legacySecondary, { borderColor: colors.border }]} onPress={onClose} activeOpacity={0.85}>
                <Text style={[styles.legacySecondaryText, { color: colors.textPrimary }]}>Продолжить путь</Text>
              </TouchableOpacity>
              <GamificationShareButton
                type={RECORD_STYLE.shareType}
                id="longest"
                filled
                fallbackMessage={`${RECORD_STYLE.headline} ${daysLabel ?? ''} на Пути Света в Agapella`.trim()}
                style={styles.legacyShare}
              />
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  scrim: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.64)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 22,
  },
  scrimMobile: { padding: 8 },
  legacyScrim: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  milestoneCard: {
    width: '100%',
    maxWidth: 620,
    borderRadius: 26,
    borderWidth: 1,
    overflow: 'hidden',
  },
  milestoneCardMobile: { maxWidth: 390, borderRadius: 28 },
  milestoneBackground: { flex: 1 },
  milestoneClose: {
    position: 'absolute',
    zIndex: 2,
    top: 22,
    right: 22,
    width: 38,
    height: 38,
    borderRadius: 19,
    borderWidth: 1,
    borderColor: '#EF7B35',
    alignItems: 'center',
    justifyContent: 'center',
  },
  milestoneCloseMobile: { top: 16, right: 16 },
  milestoneScroll: { flex: 1 },
  milestoneScrollContent: {
    flexGrow: 1,
    paddingTop: 28,
    paddingHorizontal: 42,
    paddingBottom: 28,
  },
  milestoneScrollContentMobile: {
    paddingTop: 44,
    paddingHorizontal: 20,
    paddingBottom: 22,
  },
  milestoneMain: { flexGrow: 1, alignItems: 'center' },
  eyebrow: {
    color: accentTextOn('#E8640D'),
    ...uiFont(400),
    fontSize: 12,
    lineHeight: 16,
    ...uiFont(500),
    letterSpacing: 0.96,
    textTransform: 'uppercase',
  },
  milestoneTitle: {
    marginTop: 8,
    fontFamily: MILESTONE_BRAND_FONT,
    fontSize: 36,
    lineHeight: 43,
    ...uiFont(500),
    textAlign: 'center',
  },
  milestoneTitleMobile: { ...uiFont(400), fontSize: 31, lineHeight: 38 },
  milestoneSubtitle: {
    maxWidth: 440,
    marginTop: 6,
    ...uiFont(400),
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
  },
  heroHalo: {
    width: 170,
    height: 170,
    marginTop: 18,
    marginBottom: 10,
    borderRadius: 85,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#F58A2C',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.24,
    shadowRadius: 22,
    elevation: 8,
  },
  heroHaloMobile: { width: 150, height: 150, borderRadius: 75, marginTop: 20 },
  heroHaloInner: {
    width: 150,
    height: 150,
    borderRadius: 75,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroHaloInnerMobile: { width: 132, height: 132, borderRadius: 66 },
  heroIcon: { width: 138, height: 138 },
  heroIconMobile: { width: 120, height: 120 },
  milestoneName: {
    color: accentTextOn('#DE5D08'),
    fontFamily: MILESTONE_BRAND_FONT,
    fontSize: 31,
    lineHeight: 38,
    ...uiFont(500),
    textAlign: 'center',
  },
  milestoneNameMobile: { ...uiFont(400), fontSize: 27, lineHeight: 34 },
  streakValue: {
    marginTop: 5,
    ...uiFont(400),
    fontSize: 15,
    lineHeight: 20,
    ...uiFont(500),
    textAlign: 'center',
  },
  milestoneCaption: {
    marginTop: 3,
    ...uiFont(400),
    fontSize: 13,
    lineHeight: 18,
    textAlign: 'center',
  },
  unlocked: { width: '100%', marginTop: 22, alignItems: 'flex-start' },
  unlockedMobile: { marginTop: 26 },
  unlockedLabel: {
    marginBottom: 9,
    ...uiFont(400),
    fontSize: 11,
    lineHeight: 15,
    ...uiFont(500),
    letterSpacing: 0.55,
    textTransform: 'uppercase',
  },
  reward: {
    width: '100%',
    minHeight: 76,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 13,
    paddingVertical: 13,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderRadius: 16,
  },
  rewardIcon: { width: 48, height: 48, flexShrink: 0 },
  rewardCopyBlock: { flex: 1, minWidth: 0 },
  rewardTitle: {
    ...uiFont(400),
    fontSize: 14,
    lineHeight: 19,
    ...uiFont(500),
    flexShrink: 1,
  },
  rewardCopy: {
    marginTop: 3,
    ...uiFont(400),
    fontSize: 12,
    lineHeight: 17,
    flexShrink: 1,
  },
  milestoneActions: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 12,
    marginTop: 26,
  },
  milestoneActionsMobile: { flexDirection: 'column' },
  milestoneActionMobile: { flex: 0, width: '100%' },
  milestoneSecondary: {
    flex: 1,
    minHeight: 50,
    paddingHorizontal: 12,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#F0782B',
    alignItems: 'center',
    justifyContent: 'center',
  },
  milestoneSecondaryText: {
    ...uiFont(400),
    fontSize: 14,
    lineHeight: 19,
    ...uiFont(500),
    textAlign: 'center',
  },
  milestonePrimaryShell: {
    flex: 1,
    minHeight: 50,
    borderRadius: 14,
    overflow: 'hidden',
  },
  milestonePrimary: {
    flex: 1,
    width: '100%',
    minHeight: 50,
    paddingVertical: 0,
    paddingHorizontal: 12,
    borderRadius: 14,
    backgroundColor: 'transparent',
  },
  milestonePrimaryText: {
    ...uiFont(400),
    fontSize: 14,
    lineHeight: 19,
    ...uiFont(500),
  },

  // Legacy new_record styles.
  legacyCard: { width: '100%', maxWidth: 460, maxHeight: '92%', borderRadius: 22, borderWidth: 1, overflow: 'hidden' },
  legacyClose: { position: 'absolute', top: 14, right: 14, zIndex: 2, width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  legacyBody: { padding: 24, paddingTop: 32, alignItems: 'center', gap: 6 },
  legacyCongrats: { ...uiFont(500, 700), fontSize: 26 },
  legacyReached: { ...uiFont(500, 700), fontSize: 15 },
  legacyBadgeGlow: { marginVertical: 14, width: 112, height: 112, borderRadius: 56, alignItems: 'center', justifyContent: 'center' },
  legacyBadge: { width: 112, height: 112, borderRadius: 56, borderWidth: 2, alignItems: 'center', justifyContent: 'center' },
  legacyDays: { ...uiFont(500, 700), fontSize: 22, marginTop: 2 },
  legacyMessage: { ...uiFont(400), fontSize: 14, lineHeight: 20, textAlign: 'center', marginTop: 6 },
  legacyActions: { flexDirection: 'row', gap: 10, marginTop: 24, alignSelf: 'stretch' },
  legacySecondary: { flex: 1, paddingVertical: 13, borderRadius: 12, borderWidth: 1, alignItems: 'center' },
  legacySecondaryText: { ...uiFont(500, 700), fontSize: 13 },
  legacyShare: { flex: 1, paddingVertical: 13, borderRadius: 12 },
});
